import { useLocalSearchParams } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import MemberAdd from '@/components/MemberAdd/MemberAdd';
import type {
  ManagedMemberInput,
  MemberAddEntity,
  MemberAddState,
} from '@/components/MemberAdd/MemberAdd.types';
import type { MemberType } from '@/components/People/PeopleFilterSelector';
import { graphql } from '@/gql/graphql';

const TeamPersonAddInsert = graphql(/* GraphQL */ `
  mutation TeamPersonAddInsert(
    $input: [person_team_enrollment_insert_input!]!
  ) {
    insert_person_team_enrollment(objects: $input) {
      affected_rows
    }
  }
`);

const GetTeamPersonAdd = graphql(/* GraphQL */ `
  query GetTeamPersonAdd($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      id
      title
      root_event_id
    }
  }
`);

export default function TeamPersonAdd() {
  const [, teamPersonAddInsert] = useMutation(TeamPersonAddInsert);
  const { memberType, teamId } = useLocalSearchParams<{
    memberType?: MemberType;
    teamId: string;
  }>();
  const initialMemberType: MemberType =
    memberType === 'non_participant' ? 'non_participant' : 'participant';
  const [{ data, error, fetching }, retry] = useQuery({
    query: GetTeamPersonAdd,
    variables: { teamId },
    pause: !teamId,
  });
  const team = data?.team_by_pk;
  const entity: MemberAddEntity | null = team
    ? {
        id: team.id,
        kind: 'team',
        title: team.title,
      }
    : null;
  const insertMembers = async (
    members: ManagedMemberInput[],
    selectedMemberType: MemberType,
  ) => {
    const { data } = await teamPersonAddInsert({
      input: members.map((member) => ({
        team_id: teamId,
        member_type: selectedMemberType,
        person: {
          data: {
            id: member.id,
            avatar_id: member.avatarId,
            color: member.color,
            first_name: member.firstName,
            last_name: member.lastName,
            is_managed: true,
            root_event_id: team?.root_event_id ?? null,
          },
        },
      })),
    });

    return Boolean(data?.insert_person_team_enrollment?.affected_rows);
  };
  const onRetry = () => retry({ requestPolicy: 'network-only' });
  const state: MemberAddState = fetching
    ? { status: 'loading' }
    : error
      ? { status: 'error', onRetry }
      : entity
        ? { status: 'ready', entity, insertMembers }
        : { status: 'not-found', onRetry };

  return <MemberAdd initialMemberType={initialMemberType} state={state} />;
}
